import React, { useMemo, useCallback, useState, useRef, useEffect, Fragment, memo } from 'react'
import { createEditor, Transforms, Editor, Node, Operation } from 'slate'
import { Slate, Editable, withReact, ReactEditor } from 'slate-react'
import { withHistory } from 'slate-history'
import { cx, css } from '@emotion/css'
import { cloneDeep, isEqual } from 'lodash'
import { v4 } from 'uuid'
import siteViewStyles from '../../styles.module.css'
import { savedSelection } from '../../utils/savedSelection'
//@ts-ignore
import styles from './style.module.css'
import { Element, Leaf } from '../../elements'
import { MainToolbar, HoveringToolbar } from '../Toolbar'
import { applyPlugins } from '../../utils'
import { HtmlEditComponent } from '../../elements/utils/editHtml'
import { EditorProvider, ScrollRefProvider, ReduxProvider } from '../ContextProvider'
import { handleShortcut } from '../../utils/shortcuts/handleShortcut'
import { Icon } from '../../../../..'
import { useEditorResize, handleRteOverlayClick, useRteFullScreen, handleModalZIndexOnFullscreen } from '../../../../../utils/hooks'
import dndStyles from '../Dnd/style.module.css'
import { htmlToJson, jsonToHtml } from '../../utils/serialize/jsonHtmlTransformations'
import isHotkey from 'is-hotkey'
import ErrorBoundary from './ErrorBoundary'
import { useDidUpdate } from '../../../../../utils/hooks/componentLifecycle'
import { Registry } from '..'
import { ResizeIcon } from '../../elements/utils/Icon'
import useRTEEmbedListeners from './hooks/useRTEEmbedListeners'

export declare interface EditorElementProps {
  customToolbarComponents?: any
  value
  hideToolbarOnBlur?: boolean
  onChange?
  onError?
  toolbar: boolean
  hoveringToolbar: boolean
  contentStack: boolean
  toolbarMode: any
  templateRegistryCondition: any
  requestProps: any
  onFocus: any
  onBlur: any
  onMouseEnter: any
  onMouseLeave: any
  className: any
  required: boolean
  allowExtraTags: object
  customPasteOptions: object
  contentTypeNameMap: object
  disabled: boolean
  fieldName: string
  isWrappingInlineElementsEnabled: boolean
  csOnlyBreakline: boolean
  csBreakLineOnEnter: boolean
  copyPasteConfig: object
  updateValueFromAppSdk: number
  useV2EmbedPicker?: boolean
  referenceActions?: {
    editBlockReference(): void
    editInlineReference(): void
    editLinkReference(): void
    editAssetReference(): void
  },
  fieldMetadata?: any,
  entryMetadata?: any,
  isDraft?: boolean,
  imageSizeInputEnabled?: boolean
  customChooseFromAssetsModal?: React.ComponentType<any>,
  customEmbedAssetsModal?: React.ComponentType<any>
  openAMV2UploadTarget?: (editor: any) => void
  isEntryInModal?: boolean
  isEntryInModalEnabled?: boolean
  /** Main app `/entry/create` — route RTE navigation through host unsaved guard. */
  isMainAppCreateEntryRoute?: boolean
}

export const initialHtmlValue = 'scrte-_-initial-value'
let toolbarHeight = 37

const FullScreenToggle = memo(({ editorRef, isFullScreen, isEntryInModal }: { editorRef: React.RefObject<HTMLDivElement>, isFullScreen: boolean, isEntryInModal?: boolean }) => {
  const fullscreen: Record<string, any> = useMemo(() => {
    return Registry.get({
      id: 'fullscreen',
      registry: 'element',
    })
  }, []); 

  const Icon = fullscreen?.iconName;
  const ActiveIcon = fullscreen?.activeIcon;

  const handleClick = useCallback((e) => {
    e.preventDefault();
    if (!editorRef.current) return;
    
    let classes = editorRef.current.classList;
    if (classes.contains('fullscreen')) {
      classes.remove('fullscreen')
    } else {
      classes.add('fullscreen')
    }
    
    // Handle modal z-index when in modal context
    if (isEntryInModal) {
      handleModalZIndexOnFullscreen(editorRef);
    }
  }, [editorRef, isEntryInModal]);

  return (
    <a className={styles['scrte-fullscreen-row-button']} onClick={handleClick} >
      {isFullScreen ? ActiveIcon : Icon}
      <span className={styles['scrte-fullscreen-row-text']} >{isFullScreen ? "Exit Full Screen" : "Expand Text Editor"}</span>
    </a>
  )
});

const EditorElement = (props: EditorElementProps) => {

  const {
    customToolbarComponents,
    value = [
      {
        type: 'docs',
        children: [
          {
            type: 'h1',
            attrs: {},
            children: [{ text: 'Hello, world!' }]
          }
        ]
      }
    ],
    hideToolbarOnBlur = false,
    onChange = () => { },
    toolbar = true,
    hoveringToolbar = true,
    contentStack,
    toolbarMode,
    templateRegistryCondition,
    allowExtraTags = {},
    customPasteOptions = {},
    csOnlyBreakline,
    csBreakLineOnEnter,
    isWrappingInlineElementsEnabled,
    contentTypeNameMap = {},
    disabled = false,
    fieldName = 'RichTextEditor',
    copyPasteConfig,
    onError,
    referenceActions,
    imageSizeInputEnabled,
    fieldMetadata,
    entryMetadata,
    customChooseFromAssetsModal,
    customEmbedAssetsModal,
    openAMV2UploadTarget,
    requestProps = {},
    onBlur = () => { },
    onMouseEnter = () => { },
    onMouseLeave = () => { },
    // placeholder = `Type '/' for options`,
    // uid = null,
    required = false,
  } = props;

  const scrollRef = useRef(null)
  const editorRef = useRef(null)
  const editorResizeRef = useRef(null)
  const singleLineRef = useRef(null)
  const [slateValue, setSlateValue] = useState(value)
  const [showEditHtml, setEditHtml] = useState(false)
  const [triggerRerenderingAfterDnd, setTriggerRerenderingAfterDnd] = useState(Date.now())
  let customToolbarComponentsCopy = cloneDeep(customToolbarComponents)

  const isFullScreen = useRteFullScreen(editorRef)

  useDidUpdate(() => {
    errorWrapper(editor, () => {
      Transforms.removeNodes(editor, { at: [0] })
      Transforms.insertNodes(editor, value, { at: [0] })
    }, onError)
  }, [props.updateValueFromAppSdk])

  useEffect(() => {
    errorWrapper(editor, () => {
      editor.selection = null
      setSlateValue(value)
      editor.children = value
    }, onError)
  }, [value])


  const [valueHtml, setValueHtml] = useState(initialHtmlValue)
  const editor = useMemo(() => applyPlugins(withHistory(withReact(createEditor() as ReactEditor))), [])

  useEffect(() => {
    editor.requestProps = requestProps
  }, [requestProps])

  useEffect(() => {
    editor.openAMV2UploadTarget = openAMV2UploadTarget
  }, [openAMV2UploadTarget])

  let customToolbarElement = cloneDeep(customToolbarComponents['element'] || [])
  let customToolbarLeaf = cloneDeep(customToolbarComponents['leaf'] || [])
  let customToolbarEditorButtons = cloneDeep(customToolbarComponents['editorButtons'] || [])

  if (customToolbarComponents?.element && Array.isArray(customToolbarComponents?.element)) {
    if (customToolbarElement.includes('table')) {
      customToolbarElement.push(
        'table-create-table',
        'table-delete-table',
        'table-insert-row-top',
        'table-insert-row-bottom',
        'table-delete-row',
        'table-insert-col-before',
        'table-insert-col-after',
        'table-create-header',
        'thead',
        'table-merge-cells',
        'tableHeaderBtn'
      )
    }

    if (customToolbarElement.includes('alignment')) {
      customToolbarElement.push('left-align', 'center-align', 'right-align', 'justify-align')
    }

    if (customToolbarElement.includes('lists')) {
      customToolbarElement.push('ol', 'ul', 'li')
    }

    if (customToolbarElement.includes('img')) {
      customToolbarElement.push('uploadImage', 'chooseImage')
    }

    if (customToolbarElement.includes('indentation')) {
      customToolbarElement.push('indent', 'outdent')
    }

  }
  customToolbarComponentsCopy = {
    element: customToolbarElement,
    leaf: customToolbarLeaf,
    editorButtons: customToolbarEditorButtons
  }
  useEffect(() => {
    if (showEditHtml) {
      let htmlValue = jsonToHtml(slateValue[0])
      setValueHtml(htmlValue)
    } else if (valueHtml !== initialHtmlValue) {
      let slateJson = htmlToJson(valueHtml, editor.allowExtraTags, { isWrappingInlineElementsEnabled })
      Transforms.removeNodes(editor, { at: [0] })
      Transforms.insertNodes(editor, slateJson, { at: [0] })
    }
  }, [showEditHtml])

  useRTEEmbedListeners({
    editor,
    editorRef,
    fieldMetadata,
    entryMetadata,
    isEntryInModal: props.isEntryInModal,
    isEntryInModalEnabled: props.isEntryInModalEnabled,
    isMainAppCreateEntryRoute: props.isMainAppCreateEntryRoute,
  })

  editor.useV2EmbedPicker = props.useV2EmbedPicker
  editor.isDraft = props.isDraft

  editor.customToolbarComponents = customToolbarComponentsCopy
  editor.toolbarMode = toolbarMode
  editor.imageSizeInputEnabled = imageSizeInputEnabled
  editor.customChooseFromAssetsModal = customChooseFromAssetsModal
  editor.customEmbedAssetsModal = customEmbedAssetsModal
  const handleKeyDown = useCallback((event) => {
    handleShortcut(event, editor, {
      customToolbarComponents: customToolbarComponentsCopy,
      contentStack,
      toolbarMode
    })
  }, [])

  const elementRenderer = useCallback(
    (props) => (
      <ErrorBoundary>
        <Element setTriggerRerenderingAfterDnd={setTriggerRerenderingAfterDnd} singleLineRef={singleLineRef} {...props} contentTypeNameMap={contentTypeNameMap} templateRegistryCondition={templateRegistryCondition} />
      </ErrorBoundary>
    ),
    [triggerRerenderingAfterDnd]
  )
  const leafRenderer = useCallback((props) => <Leaf {...props} contentTypeNameMap={contentTypeNameMap} />, [])

  let newProps = {
    ...props,
    value: slateValue,
    setSlateValue: setSlateValue,
    showEditHtml: showEditHtml,
    setEditHtml: setEditHtml,
    editorRef: editorRef
  }

  const handleFocus = (e) => {
    props.onFocus(e)
  }

  const handleEmptyClick = (event) => {
    if (event.clientX > event.target.clientWidth || event.clientY > event.target.clientHeight) return
    const endPoint = Editor.end(editor, [])
    const { path } = endPoint
    const node = Node.get(editor, path.slice(0, 2))
    ReactEditor.focus(editor)
    if (Editor.isVoid(editor, node)) {
      Transforms.select(editor, path)
    } else {
      Transforms.select(editor, endPoint)
    }
  }

  const handleFocusForAccessibility = (e) => {
    const toolbarParent = e?.target?.getElementsByClassName('toolbar-parent')
    if (toolbarParent?.length && toolbarParent[0]?.childNodes?.length) {
      Array.from(toolbarParent[0]?.childNodes).forEach((child: any, index: any) => {
        if (index === 0) return
        let i = 0
        while (i < child.childNodes?.length) {
          if (!child.childNodes[i].className.includes('divider')) {
            child.childNodes[i]?.setAttribute('tabIndex', '-1')
          }
          i++
        }
      })
    }
    const editableElement = e?.target?.querySelector('#scrte-editable')
    if (editableElement) editableElement.setAttribute('tabIndex', '-1')
  }

  const handleKeyDownForAccessibility = (e) => {
    if (e.key === 'Enter') {
      e?.target?.querySelector('span[data-icon="Html"]')?.focus()
      const toolbarParent = e?.target?.getElementsByClassName('toolbar-parent')
      if (toolbarParent?.length && toolbarParent[0]?.childNodes?.length) {
        Array.from(toolbarParent[0]?.childNodes).forEach((child: any, index: any) => {
          if (index === 0) return
          let i = 0
          while (i < child.childNodes?.length) {
            if (!child.childNodes[i].className.includes('divider')) {
              child.childNodes[i]?.setAttribute('tabIndex', '0')
            }
            i++
          }
        })
      }
      const editableElement = e?.target?.querySelector('#scrte-editable')
      if (editableElement) editableElement.setAttribute('tabIndex', '0')
    }
    if (isHotkey('shift+meta+enter', e)) {
      e.preventDefault()
      handleEmptyClick(e)
    }
    if (e.key === 'Escape') {
      e.target?.closest('#scrte-editor')?.focus()
    }
  }

  useEffect(() => {
    if (!editor.savedSelection) {
      savedSelection(editor)
    }
  }, [])

  const handleBlur = (e) => {
    onBlur(e)
    savedSelection(editor)
  }

  useEffect(() => {
    editor.uniqueId = v4().split('-').join('')
    editor.allowExtraTags = allowExtraTags
    editor.customPasteOptions = customPasteOptions || {}
    editor.contentTypeNameMap = contentTypeNameMap
    editor.csOnlyBreakline = csOnlyBreakline
    editor.copyPasteConfig = copyPasteConfig
    editor.csBreakLineOnEnter = csBreakLineOnEnter;
    editor.referenceActions = referenceActions
  }, [copyPasteConfig])

  useEditorResize(editorResizeRef, showEditHtml)
  const shouldNotRenderHtmlComponent = valueHtml === initialHtmlValue || !showEditHtml

  const editorOnChange = (newValue) => {
    errorWrapper(editor, () => {
      if (!Array.isArray(editor.operations) || !editor.operations.every(Operation.isSelectionOperation)) {
        setSlateValue(newValue);
        onChange(newValue);
      }
    }, onError)
  }

  const voidFnHandler = () => {}

  const scrollSelectionIntoView = useCallback((editor, domRange) => {

    // Only scroll if the selection is not already visible in the viewport
    if (domRange) {
      const rect = domRange.getBoundingClientRect()
      const scroller = scrollRef.current
      
      if (scroller) {
        const scrollerRect = scroller.getBoundingClientRect()
        const isVisible = 
          rect.top >= scrollerRect.top &&
          rect.bottom <= scrollerRect.bottom &&
          rect.left >= scrollerRect.left &&
          rect.right <= scrollerRect.right

        if (!isVisible) {
          const scrollTop = scroller.scrollTop
          const scrollHeight = scroller.scrollHeight
          const clientHeight = scroller.clientHeight
          const rangeTop = rect.top - scrollerRect.top + scrollTop
          
          if (rangeTop < scrollTop) {
            scroller.scrollTo({ top: rangeTop - 20, behavior: 'smooth' })
          } else if (rangeTop + rect.height > scrollTop + clientHeight) {
            scroller.scrollTo({ top: rangeTop + rect.height - clientHeight + 20, behavior: 'smooth' })
          }
        }
      }
    }
  }, [])


  return (
    <ErrorBoundary onError={onError} editor={editor}>
      <ReduxProvider>
        <EditorProvider.Provider value={newProps}>
          <ScrollRefProvider.Provider value={{ scrollRef }}>
            <Slate
              editor={editor}
              value={slateValue}
              onChange={editorOnChange}>
              <div ref={singleLineRef} contentEditable={false} className={cx(dndStyles['scrte-dragline'])} />
              <div
                tabIndex={0}
                onFocus={handleFocusForAccessibility}
                onKeyDown={handleKeyDownForAccessibility}
                ref={editorRef}
                id="scrte-editor"
                className={cx(
                  styles['super-charged-rte'],
                  'super-charged-rte active',
                  disabled && styles['disabled'],
                  css`
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    background: #fff;
                  `
                )}>
                {toolbar && (
                  <>
                    {!isFullScreen ? (
                      <div className={cx(styles['scrte-fullscreen-row'])}  >
                        <FullScreenToggle editorRef={editorRef} isFullScreen={isFullScreen} isEntryInModal={props.isEntryInModal} />
                      </div>
                    ) : null}

                    <MainToolbar
                      customToolbarComponents={customToolbarComponentsCopy}
                      className={cx('scrte-toolbar', styles['scrte-main-toolbar'])}
                      contentStack={contentStack}
                      toolbarMode={toolbarMode}
                      templateregistrycondition={templateRegistryCondition}
                      fieldName={fieldName}
                      editorRef={editorRef}
                      FullScreenToggle={<FullScreenToggle editorRef={editorRef} isFullScreen={isFullScreen} isEntryInModal={props.isEntryInModal} />}
                    />
                  </>
                )}
                {!disabled && hoveringToolbar && !showEditHtml && (
                  <HoveringToolbar
                    customToolbarComponents={customToolbarComponentsCopy}
                    scrollRef={scrollRef}
                    contentStack={contentStack}
                    toolbarMode={toolbarMode}
                  />
                )}
                <div
                  id="scroller"
                  className={cx('scrte-scroller', `scrte-editor-${editor.uniqueId}`, {
                    [styles['scrte-scroller-edit-html']]: showEditHtml
                  })}
                  ref={scrollRef}>
                  {shouldNotRenderHtmlComponent ? (
                    <Fragment>
                      <Editable
                        onDrop={voidFnHandler}
                        onDragExit={voidFnHandler}
                        onDragLeave={voidFnHandler}
                        onDragOver={voidFnHandler}
                        onDrag={voidFnHandler}
                        onDragStart={voidFnHandler}
                        onDragEnter={voidFnHandler}
                        onDragEnd={voidFnHandler}
                        onMouseDown={handleEmptyClick}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        scrollSelectionIntoView={scrollSelectionIntoView}
                        renderElement={elementRenderer}
                        renderLeaf={leafRenderer}
                        onKeyDown={handleKeyDown}
                        autoCorrect="true"
                        spellCheck="true"
                        id="scrte-editable"
                        readOnly={disabled}
                        tabIndex={-1}
                        //@ts-ignore
                        className={cx(
                          siteViewStyles['editable'],
                          props.className,
                          styles['scrte-editable'],
                          'scrte-editable'
                        )}
                        required={required}></Editable>
                      <div className="editor-resize" ref={editorResizeRef}>
                        <ResizeIcon />
                      </div>
                    </Fragment>
                  ) : (
                    <HtmlEditComponent
                      valueHtml={valueHtml}
                      setValueHtml={(html) => {
                        setValueHtml(html)
                        onChange(html)
                      }}
                    />
                  )}
                </div>
              </div>
              <div className="rte-fullscreen-overlay" onClick={() => handleRteOverlayClick(editorRef, false, props.isEntryInModal)}></div>
            </Slate>
          </ScrollRefProvider.Provider>
        </EditorProvider.Provider>
      </ReduxProvider>
    </ErrorBoundary>
  )
}

export default EditorElement

const errorWrapper = (editor, func, onError) => {
  try {
    func()
  } catch (e) {
    const { children, selection, history } = editor
    const metadata = { children, history, selection }
    onError ? onError(e, metadata) : console.error(e, metadata)
  }
}